import {returnButton, saveButton} from "../utils";
import {basicContext} from "../../../utils/contextUtils";
import Errors from "../../../utils/Errors";

const statusButton = (action, nextStatus, tKey, bsStyle = 'primary') => ({
    type: 'action',
    action,
    nextStatus,
    tKey,
    bsStyle,
    getUserConfirmation: true,
    confirmationMessage: `${action}ConfirmationMessage`
})

const submitForValidationButton = statusButton('submitForValidation', 'submittedForValidation', 'submitForValidation')
const publishButton = statusButton('publish', 'published', 'publish')
const markAsAdoptedButton = statusButton('markAsAdopted', 'adopted', 'markAsAdopted', 'info')
const archiveButton = statusButton('archive', 'archived', 'archive', 'warning')

// Workflow: draft -> submittedForValidation -> published -> adopted -> archived.
// Contacts of internal organizations publish directly (no validation), the others submit for validation.
// "Mark as adopted" and "archive" are available at every step before adoption.
// The status itself is only changed through these buttons, "save" keeps it.
export const getAnimalButtons = (statusId, canPublish) => {
    switch (statusId || 'draft') {
        case 'draft':
            return [canPublish ? publishButton : submitForValidationButton, markAsAdoptedButton, archiveButton, saveButton, returnButton]
        case 'submittedForValidation':
            return canPublish
                ? [publishButton, markAsAdoptedButton, archiveButton, saveButton, returnButton]
                : [markAsAdoptedButton, archiveButton, saveButton, returnButton]
        case 'published':
            return [markAsAdoptedButton, archiveButton, saveButton, returnButton]
        case 'adopted':
            return [archiveButton, saveButton, returnButton]
        default:
            return [saveButton, returnButton]
    }
}

// A new animal: the draft actions, except "archive" which makes no sense for an animal not saved yet
export const getNewAnimalButtons = canPublish =>
    getAnimalButtons('draft', canPublish).filter(button => button.action !== 'archive')

export const getCUser = (context, fieldPath) => global.app.C.CUser.get(
    {kpUser: new global.ObjectID(context.user.id)},
    {...basicContext(context), fieldPath: ['id', 'organization.id', 'organization.organizationType.id', ...fieldPath], ignoreNotFound: true}
)

export const isInternalCUser = cUser => cUser?.organization?.organizationType?.id === 'internal'

// Only contacts of internal organizations can publish an animal. Keenpoint accounts (e.g. "Support")
// are excluded even when their contact is attached to an internal organization.
export const canPublishAnimals = (cUser, context) => !context.user?.k && isInternalCUser(cUser)

// Same rule for the connected user, looked up once per request (list getters run once per line).
export const connectedUserCanPublishAnimals = context => {
    if (!context._canPublishAnimals) {
        context._canPublishAnimals = getCUser(context, []).then(cUser => canPublishAnimals(cUser, context))
    }
    return context._canPublishAnimals
}

// Lines the connected user has to treat: same conditions as the "animals to validate" notification.
export const isAnimalToValidateByUser = async (animal, context) =>
    animal?.adoptionStatus?.id === 'submittedForValidation' && await connectedUserCanPublishAnimals(context)

// Button of the requested action, recomputed server side from the stored status and the user
// (never trusted from the client). Null for a plain save.
export const findActionButton = async (oldObject, context) => {
    const action = context.action
    if (!action || action === 'save') return null
    const cUser = await getCUser(context, [])
    const canPublish = canPublishAnimals(cUser, context)
    const buttons = oldObject ? getAnimalButtons(oldObject.adoptionStatus?.id, canPublish) : getNewAnimalButtons(canPublish)
    const button = buttons
        .find(button => button.type === 'action' && button.action === action)
    if (!button) throw new Errors.ValidationError("Action non autorisée")
    return button
}
